import { useFieldArray, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import {
	Box,
	Button,
	Form,
	Group,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import './FollowUpSection.scss';

const DELAY_UNIT_OPTIONS = [
	{ id: 'hours', label: 'Hours' },
	{ id: 'days', label: 'Days' },
];

const followUpEntrySchema = z.object({
	delayValue: z.coerce.number().min(1, 'Minimum 1').max(168, 'Maximum 168 hours'),
	delayUnit: z.enum(['hours', 'days']),
});

const followUpSchema = z.object({
	enabled: z.boolean().default(false),
	sequence: z
		.array(followUpEntrySchema)
		.min(1, 'At least one follow-up is required')
		.max(3, 'Maximum 3 follow-ups'),
});

type FollowUpFormData = z.infer<typeof followUpSchema>;

const DEFAULT_SEQUENCE: FollowUpFormData['sequence'] = [
	{ delayValue: 24, delayUnit: 'hours' },
];

function getDefaultValues(
	followUps: { enabled?: boolean; sequence?: Array<{ delayValue: number; delayUnit: 'hours' | 'days' }> } | undefined,
): FollowUpFormData {
	return {
		enabled: followUps?.enabled ?? false,
		sequence: followUps?.sequence?.length ? followUps.sequence : DEFAULT_SEQUENCE,
	};
}

function FollowUpSequenceFields(): React.JSX.Element {
	const { control } = useFormContext<FollowUpFormData>();
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'sequence',
	});

	const isAtLimit = fields.length >= 3;

	const getRowLabel = (index: number): string => {
		if (index === 0) return 'After no reply for';
		return 'Then wait another';
	};

	return (
		<Stack itemsSpacing='12'>
			<Text size='bodySmall' weight='semibold'>
				Follow-up sequence
			</Text>
			<Text size='caption' color='secondary'>
				Each delay is relative to the previous message — not from the original silence.
			</Text>
			{fields.map((field, index) => (
				<Group key={field.id} itemsSpacing='8' itemsAlignY='center'>
					<Group.Item grow>
						<Group itemsSpacing='8' itemsAlignY='center'>
							<Group.Item>
								<Text size='bodySmall' color='secondary'>
									{getRowLabel(index)}
								</Text>
							</Group.Item>
							<Group.Item>
								<div className='follow-up-section__delay-value'>
									<Form.Field.Text
										schemaField={`sequence.${index}.delayValue`}
										labelText=''
										size='medium'
										type='number'
										placeholder='24'
									/>
								</div>
							</Group.Item>
							<Group.Item grow>
								<Form.Field.Dropdown
									schemaField={`sequence.${index}.delayUnit`}
									labelText=''
									options={DELAY_UNIT_OPTIONS}
								/>
							</Group.Item>
						</Group>
					</Group.Item>
					{fields.length > 1 && (
						<Group.Item>
							<Button
								size='small'
								variant='ghost'
								onPress={() => remove(index)}
								ariaLabel={`Remove follow-up ${index + 1}`}>
								Remove
							</Button>
						</Group.Item>
					)}
				</Group>
			))}
			{!isAtLimit && (
				<div>
					<Button
						size='small'
						variant='secondary'
						onPress={() => append({ delayValue: 48, delayUnit: 'hours' })}
						ariaLabel='Add follow-up'>
						Add follow-up
					</Button>
				</div>
			)}
		</Stack>
	);
}

export function FollowUpSection(): React.JSX.Element {
	return (
		<Box padding='24' background='secondary' rounding='12'>
			<div data-settings-disabled>
				<Form
					name='ai-bdc-follow-ups'
					schema={followUpSchema}
					defaultValues={getDefaultValues(undefined)}
					onSubmit={async () => ({ isSuccess: false })}
					title='Automated follow-ups'
					titleLevel='subgroup'
					titleWeight='semibold'
					titleMaxWidth='full'
					description='When a lead stops responding, the AI will automatically send follow-up messages at the intervals you define. You can configure up to three follow-ups per conversation. Follow-ups apply to SMS and email conversations only — the website assistant does not send follow-ups.'
					tag={{ children: 'Coming soon', size: 'small' }}
					toggle={{ fieldName: 'enabled' }}>
					<Stack itemsSpacing='16'>
						<FollowUpSequenceFields />
						<Text size='caption' color='tertiary'>
							All follow-ups are sent during your business hours and in compliance with TCPA quiet hours.
						</Text>
					</Stack>
				</Form>
			</div>
		</Box>
	);
}
